/**
 * TechnicianDashboard Page
 *
 * Main dashboard for the Technician Portal.
 * Shows today's tasks, active check-in status, and quick actions.
 *
 * @module pages/technician/TechnicianDashboard
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabase';
import './TechnicianPages.css';

const TechnicianDashboard = () => {
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ tasksToday: null, projects: null, hoursWeek: null, unreadMessages: null });
  const [loading, setLoading] = useState(true);
  // El catch de abajo solo logueaba en consola y dejaba los contadores en
  // null, que `renderStat` pinta como '--'. Un técnico ve cuatro guiones y no
  // tiene forma de saber si no tiene tareas o si el servidor no contestó.
  const [loadError, setLoadError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!user?.id) return;

    const fetchStats = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        // Get task IDs assigned to this user
        const { data: assignments } = await supabase
          .from('task_assignments')
          .select('task_id')
          .eq('user_id', user.id);

        const taskIds = (assignments || []).map(a => a.task_id);

        let tasksToday = 0;
        let projectCount = 0;

        if (taskIds.length > 0) {
          // Count tasks with status pending or in_progress (today's active tasks)
          const { count } = await supabase
            .from('tasks')
            .select('id', { count: 'exact', head: true })
            .in('id', taskIds)
            .in('status', ['pending', 'in_progress']);

          tasksToday = count || 0;

          // Count distinct projects from assigned tasks
          const { data: taskProjects } = await supabase
            .from('tasks')
            .select('project_id')
            .in('id', taskIds);

          const uniqueProjects = new Set((taskProjects || []).map(t => t.project_id));
          projectCount = uniqueProjects.size;
        }

        // Hours this week from time_entries
        const startOfWeek = new Date();
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        startOfWeek.setHours(0, 0, 0, 0);

        const { data: timeEntries } = await supabase
          .from('time_entries')
          .select('total_hours')
          .eq('user_id', user.id)
          .gte('check_in_time', startOfWeek.toISOString())
          .not('total_hours', 'is', null);

        const hoursWeek = Math.round(
          (timeEntries || []).reduce((sum, e) => sum + (Number(e.total_hours) || 0), 0) * 10
        ) / 10;

        // Unread messages: conversations where user is participant, messages after last_read_at
        let unreadMessages = 0;
        const { data: participations } = await supabase
          .from('chat_participants')
          .select('conversation_id, last_read_at')
          .eq('user_id', user.id);

        if (participations && participations.length > 0) {
          const unreadCounts = await Promise.all(
            participations.map(async (p) => {
              const query = supabase
                .from('chat_messages')
                .select('id', { count: 'exact', head: true })
                .eq('conversation_id', p.conversation_id)
                .neq('sender_id', user.id)
                .eq('is_deleted', false);

              if (p.last_read_at) {
                query.gt('created_at', p.last_read_at);
              }

              const { count: c } = await query;
              return c || 0;
            })
          );
          unreadMessages = unreadCounts.reduce((sum, c) => sum + c, 0);
        }

        setStats({ tasksToday, projects: projectCount, hoursWeek, unreadMessages });
      } catch (err) {
        console.error('[TechnicianDashboard] Stats error:', err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user?.id, reloadToken]);

  const renderStat = (value) => {
    if (loading) return <Spinner animation="border" size="sm" />;
    // Con `loadError` el bloque de arriba ya explica qué pasó; el guion acá
    // deja de ser el único mensaje.
    if (loadError) return '—';
    return value ?? 0;
  };

  // Las tarjetas son <div role="button">. Anunciarse como botón sin poder
  // activarse con el teclado es peor que no anunciarse: `tabIndex` las mete
  // en el orden de foco y este handler replica lo que un <button> hace de
  // fábrica (Enter y Espacio).
  const handleCardKeyDown = useCallback((path) => (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      navigate(path);
    }
  }, [navigate]);

  return (
    <Container fluid className="technician-page">
      <div className="page-header">
        <h1 className="page-title">
          Bienvenido, {profile?.full_name?.split(' ')[0] || 'Tecnico'}
        </h1>
        <p className="page-subtitle">Portal del Tecnico</p>
      </div>

      {loadError && (
        <Alert variant="warning" className="mb-3" data-testid="technician-stats-error">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="flex-grow-1">
              No se pudieron cargar tus datos. Es un problema de conexión, no que no
              tengas trabajo asignado.
            </span>
            <Button
              variant="outline-warning"
              size="sm"
              onClick={() => setReloadToken((n) => n + 1)}
              data-testid="technician-stats-retry"
            >
              <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
              Reintentar
            </Button>
          </div>
        </Alert>
      )}

      <Row className="g-3">
        <Col xs={6}>
          <Card
            className="stat-card"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/tecnico/tareas')}
            onKeyDown={handleCardKeyDown('/tecnico/tareas')}
          >
            <Card.Body className="text-center">
              <i className="bi bi-list-task text-primary mb-2"></i>
              <div className="stat-number">{renderStat(stats.tasksToday)}</div>
              <div className="stat-label">Tareas Hoy</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6}>
          <Card
            className="stat-card"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/tecnico/horas')}
            onKeyDown={handleCardKeyDown('/tecnico/horas')}
          >
            <Card.Body className="text-center">
              <i className="bi bi-clock-history text-success mb-2"></i>
              <div className="stat-number">{renderStat(stats.hoursWeek)}</div>
              <div className="stat-label">Horas Semana</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6}>
          <Card
            className="stat-card"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/tecnico/proyectos')}
            onKeyDown={handleCardKeyDown('/tecnico/proyectos')}
          >
            <Card.Body className="text-center">
              <i className="bi bi-folder text-info mb-2"></i>
              <div className="stat-number">{renderStat(stats.projects)}</div>
              <div className="stat-label">Proyectos</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6}>
          <Card
            className="stat-card"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/tecnico/mensajes')}
            onKeyDown={handleCardKeyDown('/tecnico/mensajes')}
          >
            <Card.Body className="text-center">
              <i className="bi bi-chat-dots text-warning mb-2"></i>
              <div className="stat-number">{renderStat(stats.unreadMessages)}</div>
              <div className="stat-label">Mensajes</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TechnicianDashboard;
